// LicenseOrgsPage.tsx — List of every license org visible to the
// admin, with search + status filter.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFirestoreQuery } from '@/hooks/useFirestoreQuery';
import { listLicenseOrgs } from '@/services/firestore/licenseOrgs';
import { SkeletonList } from '@/components/SkeletonRow';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { StatusPill } from '@/components/pills';
import { fmtDate } from '@/utils/format';
import type { LicenseOrg } from '@/types/domain';

type StatusFilter = 'all' | 'active' | 'suspended';

export function LicenseOrgsPage() {
  const { data, loading, error } = useFirestoreQuery(
    'licenseOrgs:list',
    listLicenseOrgs,
  );

  const orgs = useMemo(() => {
    if (!data) return [] as LicenseOrg[];
    return [...data].sort((a, b) => {
      if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
      const am = a.createdAt?.toMillis?.() ?? 0;
      const bm = b.createdAt?.toMillis?.() ?? 0;
      return bm - am;
    });
  }, [data]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return orgs.filter((o) => {
      if (statusFilter !== 'all' && o.status !== statusFilter) return false;
      if (!q) return true;
      return (o.id + ' ' + o.name).toLowerCase().includes(q);
    });
  }, [orgs, search, statusFilter]);

  const activeCount = orgs.filter((o) => o.status === 'active').length;
  const suspendedCount = orgs.filter((o) => o.status === 'suspended').length;

  return (
    <>
      <header className="console-page-header">
        <div>
          <h1 className="console-page-title">License Orgs</h1>
          <div className="console-page-subtitle">
            Group / community license owners. Tap a row to drill in.
          </div>
        </div>
      </header>

      <div className="console-filter-bar">
        <div className="console-search">
          <span className="material-icons">search</span>
          <input
            type="search"
            placeholder="Search by name or id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            maxLength={120}
          />
        </div>
        <div className="console-chip-group">
          <button
            className={`console-chip${statusFilter === 'all' ? ' is-active' : ''}`}
            type="button"
            onClick={() => setStatusFilter('all')}
          >
            All {orgs.length}
          </button>
          <button
            className={`console-chip${statusFilter === 'active' ? ' is-active' : ''}`}
            type="button"
            onClick={() => setStatusFilter('active')}
          >
            Active {activeCount}
          </button>
          <button
            className={`console-chip${statusFilter === 'suspended' ? ' is-active' : ''}`}
            type="button"
            onClick={() => setStatusFilter('suspended')}
          >
            Suspended {suspendedCount}
          </button>
        </div>
      </div>

      {loading && !data && <SkeletonList count={5} />}
      {error && <ErrorState message={`Could not load license orgs: ${error.message}`} />}
      {data && filtered.length === 0 && (
        <EmptyState
          icon="inbox"
          title="No license orgs match"
          body="Try clearing filters or seeding an org first."
        />
      )}
      {data && filtered.length > 0 && <OrgTable orgs={filtered} />}
    </>
  );
}

function OrgTable({ orgs }: { orgs: LicenseOrg[] }) {
  return (
    <div className="console-table-wrap">
      <table className="console-table">
        <thead>
          <tr>
            <th>Org</th>
            <th>Status</th>
            <th>Owner</th>
            <th>Capacity</th>
            <th>Created</th>
            <th style={{ textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {orgs.map((o) => {
            const orgPath = `/license-orgs/${encodeURIComponent(o.id)}`;
            return (
              <tr key={o.id}>
                <td>
                  <strong>{o.name || o.id}</strong>
                  {o.name && (
                    <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
                      {o.id}
                    </div>
                  )}
                </td>
                <td><StatusPill status={o.status} /></td>
                <td>
                  {o.ownerUid ? (
                    <code style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {o.ownerUid.slice(0, 12)}…
                    </code>
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  {o.seatCapacity != null ? `${o.seatCapacity} seats` : '-'}
                </td>
                <td>{fmtDate(o.createdAt)}</td>
                <td className="actions">
                  <Link className="console-btn console-btn-ghost" to={orgPath}>View</Link>
                  <Link className="console-btn console-btn-ghost" to={`${orgPath}/members`}>Members</Link>
                  <Link className="console-btn console-btn-ghost" to={`${orgPath}/invites`}>Invites</Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
